import { afterSettle } from '@/afterSettle';

type ErrorContext = Record<string, unknown> | undefined;

const enabled = Boolean(import.meta.env.VITE_SENTRY_DSN);

export const sentryEnabled = enabled;

export interface Vital {
  name: string;
  value: number;
  rating: string;
  route: string;
}

let vitalImpl: ((vital: Vital) => void) | null = null;
const vitals: Vital[] = [];
const VITALS_MAX = 10;

type Sdk = typeof import('./sentryImpl');

let sdk: Sdk | null = null;
const queue: ((s: Sdk) => void)[] = [];
const QUEUE_MAX = 50;

let signedInToken: string | null = null;

let started = false;
let requested = false;

function send(call: (s: Sdk) => void): void {
  if (!enabled) return;
  if (sdk) {
    call(sdk);
    return;
  }
  if (queue.length < QUEUE_MAX) queue.push(call);
  if (started) loadSdk();
}

export function reportError(error: unknown, context?: ErrorContext): void {
  send((s) => s.report(error, context));
}

export function reportWarning(message: string, context?: ErrorContext): void {
  send((s) => s.warn(message, context));
}

export function identifyUser(token: string): void {
  if (!enabled) return;
  signedInToken = token;
  sdk?.identify(token);
}

export function reportVital(vital: Vital): void {
  if (!enabled) return;
  if (vitalImpl) {
    vitalImpl(vital);
    return;
  }
  if (vitals.length < VITALS_MAX) vitals.push(vital);
  if (started) loadSdk();
}

const onError = (e: ErrorEvent) => reportError(e.error ?? e.message);
const onRejection = (e: PromiseRejectionEvent) => reportError(e.reason);

function detach(): void {
  window.removeEventListener('error', onError);
  window.removeEventListener('unhandledrejection', onRejection);
}

function loadSdk(): void {
  if (requested) return;
  requested = true;
  void import('./sentryImpl').then(
    (m) => {
      m.init();
      detach();
      if (signedInToken) m.identify(signedInToken);
      sdk = m;
      for (const call of queue) call(m);
      queue.length = 0;
      vitalImpl = m.vital;
      for (const vital of vitals) vitalImpl(vital);
      vitals.length = 0;
    },
    () => {
      detach();
      queue.length = 0;
      vitals.length = 0;
    },
  );
}

export function initSentry(): void {
  if (!enabled) return;
  started = true;
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  if (queue.length > 0) loadSdk();
  else afterSettle(loadSdk);
}
